import { useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/stores/auth'
import { dedupe, getCached, setCached, invalidateCache } from '@/lib/query-cache'

/**
 * Driver denne kontoen noe, eller er hun bare invitert til noe?
 *
 * ## Hvorfor spørsmålet finnes
 *
 * `/kommunikasjon`, `/offers` og `/catalogue` er arrangørflater, og de sto
 * åpne for enhver innlogget konto. For de 80 på Nortura-salgsmøtet betyr det at
 * en deltaker som logger inn fra en SMS får «Kommunikasjon» i menyen sin — en
 * flate for å sende meldinger til deltakerlista til en kunde hun ikke er
 * ansatt hos. David, 12.08.2026: «det er bare eiere av en tenant eller admins
 * av en tenant som kan bruke /kommunikasjon … samme gjelder /catalogue og
 * /offers».
 *
 * ## Regelen bor i basen, ikke her
 *
 * `is_account_operator()` er ett sted, og menyen, ruteporten og en framtidig
 * serversjekk spør den samme. Denne filen er transporten, ikke regelen — se
 * `20260812_is_account_operator.sql` for hvorfor «operatør» er bredere enn
 * ordet «tenant» (kort: 177 av 189 kontoer driver ingenting, og en ren
 * tenant-regel ville tatt /offers fra Davids egen leverandørkonto).
 *
 * ## Formen: nullstilling under render, henting i effekten
 *
 * Kontobytte nullstiller svaret UNDER RENDER, som er Reacts egen anbefaling for
 * avledet tilstand, og ikke i en effekt — `react-hooks/set-state-in-effect` er
 * en port i dette bygget, og den porten har rett: en synkron setState i en
 * effekt gir en ekstra render på hver eneste navigasjon. Effekten under gjør
 * bare det den er til for, nemlig å snakke med et eksternt system, og setter
 * tilstand i tilbakekallet.
 *
 * Standardsvaret mens den laster er «nei», så menyen ikke blinker inn og ut for
 * de 181 som ikke skal ha den. `loading` skilles ut for ruteporten, som må
 * vente: å redirigere på `false` før svaret er inne kaster ut en operatør som
 * bare var treg på nett.
 */

const CACHE_KEY = 'is-account-operator:'
/**
 * Medlemskap og eierskap endrer seg når noen inviteres inn i en org eller lager
 * sitt første arrangement — ikke mellom to komponentmonteringer på samme skjerm.
 * Samme levetid som `useMyOrganization`, som svarer på nabospørsmålet.
 */
const TTL_MS = 60_000

/** Kall denne når kontoen kan ha blitt operatør: nytt arrangement, ny org. */
export function invalidateIsOperator(): void {
  invalidateCache(CACHE_KEY)
}

interface Answer {
  /** Hvem svaret gjelder. `null` = utlogget. */
  userId: string | null
  /** `null` = ikke spurt ennå. */
  value: boolean | null
}

function initialAnswer(userId: string | null): Answer {
  if (!userId) return { userId, value: false }
  // `getCached` svarer `null` på bom, og `false` er et gyldig svar her — så
  // treffet må testes mot null og ikke mot falsy, ellers spør en ikke-operatør
  // på nytt ved hver eneste navigasjon.
  return { userId, value: getCached<boolean>(CACHE_KEY + userId, TTL_MS) }
}

export function useIsOperator(): { isOperator: boolean; loading: boolean } {
  const userId = useAuthStore((s) => s.user?.id ?? null)
  const [answer, setAnswer] = useState<Answer>(() => initialAnswer(userId))

  // Kontoen byttet (eller logget ut): kast svaret som gjaldt den forrige, her og
  // nå. React kjører render på nytt umiddelbart, før noe males.
  if (answer.userId !== userId) setAnswer(initialAnswer(userId))

  useEffect(() => {
    if (!userId || answer.value !== null) return
    let alive = true
    const key = CACHE_KEY + userId
    void dedupe(key, async () => {
      const { data, error } = await supabase.rpc('is_account_operator', { p_user_id: userId })
      // Et feilet oppslag er verken ja eller nei. Det svares nei, fordi å vise
      // en flate hun ikke har lov til er verre enn å skjule en hun har — og
      // ruteporten håndhever uansett. Feilen caches ikke, så neste navigasjon
      // spør på nytt.
      const value = error ? false : !!data
      if (!error) setCached(key, value)
      return value
    }).then((value) => {
      if (alive) setAnswer({ userId, value })
    })
    return () => {
      alive = false
    }
  }, [userId, answer.value])

  return { isOperator: answer.value ?? false, loading: answer.value === null }
}
